import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, RefreshCw, Plus, Edit2, Trash2, Download, 
  MapPin, Phone, Clock, FileText, CheckCircle, AlertCircle,
  Lock, X, Save, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTIES = ['All', 'Osceola', 'Orange', 'Polk', 'Brevard'];

// Living Fruit Ministry colors for this tool
const colors = {
  forestGreen: '#1a4a2e',
  gold: '#c9a84c',
  lightGreen: '#2d6a4f',
  cream: '#faf8f5'
};

export default function PantryFinderPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  
  const [pantries, setPantries] = useState([]);
  const [filteredPantries, setFilteredPantries] = useState([]);
  const [selectedCounty, setSelectedCounty] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Edit/Add modal
  const [showModal, setShowModal] = useState(false);
  const [editingPantry, setEditingPantry] = useState(null);
  const [formData, setFormData] = useState({
    name: '', address: '', city: '', zip: '', county: 'Osceola',
    phone: '', hours: '', notes: '', verified: false
  });

  // Check localStorage for existing session
  useEffect(() => {
    const stored = localStorage.getItem('pantryFinderAuth');
    if (stored === 'true') {
      setIsAuthenticated(true);
      loadPantries();
    }
  }, []);

  // Filter pantries when county or search changes
  useEffect(() => {
    let filtered = [...pantries];
    
    if (selectedCounty !== 'All') {
      filtered = filtered.filter(p => p.county === selectedCounty);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.city?.toLowerCase().includes(query) ||
        p.zip?.includes(query) ||
        p.address?.toLowerCase().includes(query)
      );
    }
    
    setFilteredPantries(filtered);
  }, [pantries, selectedCounty, searchQuery]);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinError('');
    
    try {
      const response = await axios.post(`${API}/pantry-finder/verify-pin`, { pin });
      if (response.data.success) {
        setIsAuthenticated(true);
        localStorage.setItem('pantryFinderAuth', 'true');
        toast.success('Access granted!');
        loadPantries();
      }
    } catch (error) {
      setPinError('Invalid PIN. Please try again.');
    }
  };

  const loadPantries = async () => {
    setIsLoading(true);
    try {
      // Check localStorage first
      const stored = localStorage.getItem('pantryFinderData');
      const storedTime = localStorage.getItem('pantryFinderLastUpdated');
      
      if (stored && storedTime) {
        setPantries(JSON.parse(stored));
        setLastUpdated(new Date(storedTime));
        
        // Check if we need to refresh (7 days)
        const daysSinceUpdate = (Date.now() - new Date(storedTime).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceUpdate >= 7) {
          refreshPantries();
        }
      } else {
        // Load sample data initially
        const response = await axios.get(`${API}/pantry-finder/sample-data`);
        if (response.data.pantries) {
          setPantries(response.data.pantries);
          setLastUpdated(new Date());
          localStorage.setItem('pantryFinderData', JSON.stringify(response.data.pantries));
          localStorage.setItem('pantryFinderLastUpdated', new Date().toISOString());
        }
      }
    } catch (error) {
      console.error('Failed to load pantries:', error);
      toast.error('Failed to load pantry data');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshPantries = async () => {
    setIsRefreshing(true);
    try {
      toast.info('Searching for pantries with AI... This may take a moment.');
      const response = await axios.post(`${API}/pantry-finder/search`);
      
      if (response.data.success && response.data.pantries) {
        // Merge with existing manual entries
        const existingManual = pantries.filter(p => p.source === 'manual');
        const newPantries = [...response.data.pantries, ...existingManual];
        
        setPantries(newPantries);
        setLastUpdated(new Date());
        localStorage.setItem('pantryFinderData', JSON.stringify(newPantries));
        localStorage.setItem('pantryFinderLastUpdated', new Date().toISOString());
        toast.success(`Found ${response.data.pantries.length} pantries!`);
      } else {
        toast.error(response.data.error || 'AI search returned no results');
      }
    } catch (error) {
      console.error('Refresh failed:', error);
      toast.error('Failed to refresh pantry data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAddPantry = () => {
    setEditingPantry(null);
    setFormData({
      name: '', address: '', city: '', zip: '', county: 'Osceola',
      phone: '', hours: '', notes: '', verified: false
    });
    setShowModal(true);
  };

  const handleEditPantry = (pantry) => {
    setEditingPantry(pantry);
    setFormData({ ...pantry });
    setShowModal(true);
  };

  const handleSavePantry = () => {
    if (!formData.name || !formData.address || !formData.city) {
      toast.error('Please fill in required fields');
      return;
    }

    let updated;
    if (editingPantry) {
      // Update existing
      updated = pantries.map(p => 
        p.id === editingPantry.id ? { ...formData, id: p.id, source: p.source } : p
      );
    } else {
      // Add new
      const newPantry = {
        ...formData,
        id: `manual-${Date.now()}`,
        source: 'manual'
      };
      updated = [...pantries, newPantry];
    }

    setPantries(updated);
    localStorage.setItem('pantryFinderData', JSON.stringify(updated));
    setShowModal(false);
    toast.success(editingPantry ? 'Pantry updated!' : 'Pantry added!');
  };

  const handleDeletePantry = (pantry) => {
    if (window.confirm(`Delete "${pantry.name}"?`)) {
      const updated = pantries.filter(p => p.id !== pantry.id);
      setPantries(updated);
      localStorage.setItem('pantryFinderData', JSON.stringify(updated));
      toast.success('Pantry deleted');
    }
  };

  const handleToggleVerified = (pantry) => {
    const updated = pantries.map(p => 
      p.id === pantry.id ? { ...p, verified: !p.verified } : p
    );
    setPantries(updated);
    localStorage.setItem('pantryFinderData', JSON.stringify(updated));
    toast.success(pantry.verified ? 'Marked as unverified' : 'Marked as verified');
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Address', 'City', 'ZIP', 'County', 'Phone', 'Hours', 'Notes', 'Verified'];
    const rows = filteredPantries.map(p => [
      p.name, p.address, p.city, p.zip, p.county, p.phone, p.hours, p.notes, p.verified ? 'Yes' : 'No'
    ]);
    
    const csv = [headers, ...rows].map(row => 
      row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `food-pantries-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded!');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('pantryFinderAuth');
    setPin('');
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ background: `linear-gradient(135deg, ${colors.forestGreen} 0%, ${colors.lightGreen} 100%)` }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
        >
          {/* Logo Placeholder */}
          <div className="text-center mb-8">
            <div 
              className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: colors.gold }}
            >
              <span className="text-3xl">🌳</span>
            </div>
            <h1 className="text-2xl font-bold" style={{ color: colors.forestGreen }}>
              Living Fruit Ministry
            </h1>
            <p className="text-gray-600 mt-2">Central Florida Food Pantry Finder</p>
          </div>

          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock size={16} className="inline mr-2" />
                Staff PIN
              </label>
              <Input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter PIN"
                className="text-center text-2xl tracking-widest"
                maxLength={6}
                data-testid="pin-input"
              />
              {pinError && (
                <p className="text-red-500 text-sm mt-2">{pinError}</p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full text-white"
              style={{ backgroundColor: colors.forestGreen }}
              data-testid="login-btn"
            >
              Access Dashboard
            </Button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            Internal staff tool • Contact admin for access
          </p>
        </motion.div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 shadow-lg"
        style={{ backgroundColor: colors.forestGreen }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: colors.gold }}
              >
                <span className="text-xl">🌳</span>
              </div>
              <div>
                <h1 className="text-white font-bold text-lg md:text-xl">
                  Central Florida Food Pantry Finder
                </h1>
                <p className="text-white/70 text-xs">Living Fruit Ministry</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-white/80 hover:text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Bar */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-sm text-gray-500">Total Pantries</p>
                <p className="text-2xl font-bold" style={{ color: colors.forestGreen }}>
                  {pantries.length}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Verified</p>
                <p className="text-2xl font-bold text-green-600">
                  {pantries.filter(p => p.verified).length}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Updated</p>
                <p className="text-sm font-medium text-gray-700">
                  {lastUpdated ? lastUpdated.toLocaleDateString() : 'Never'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={refreshPantries}
                disabled={isRefreshing}
                className="text-white"
                style={{ backgroundColor: colors.gold }}
                data-testid="refresh-btn"
              >
                {isRefreshing ? (
                  <Loader2 size={18} className="mr-2 animate-spin" />
                ) : (
                  <RefreshCw size={18} className="mr-2" />
                )}
                {isRefreshing ? 'Searching...' : 'AI Refresh'}
              </Button>
              <Button
                onClick={handleAddPantry}
                className="text-white"
                style={{ backgroundColor: colors.forestGreen }}
                data-testid="add-pantry-btn"
              >
                <Plus size={18} className="mr-2" />
                Add Pantry
              </Button>
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="border-2"
                style={{ borderColor: colors.forestGreen, color: colors.forestGreen }}
                data-testid="export-btn"
              >
                <Download size={18} className="mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* County Tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {COUNTIES.map(county => (
                <button
                  key={county}
                  onClick={() => setSelectedCounty(county)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    selectedCounty === county 
                      ? 'text-white shadow-md' 
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  style={selectedCounty === county ? { backgroundColor: colors.forestGreen } : {}}
                  data-testid={`county-tab-${county.toLowerCase()}`}
                >
                  {county}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name, city, or ZIP..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={40} className="animate-spin" style={{ color: colors.forestGreen }} />
          </div>
        )}

        {/* Refreshing Overlay */}
        {isRefreshing && (
          <div className="bg-white rounded-xl shadow-md p-8 mb-6 text-center">
            <Loader2 size={48} className="animate-spin mx-auto mb-4" style={{ color: colors.gold }} />
            <p className="text-lg font-medium" style={{ color: colors.forestGreen }}>
              Refreshing pantry data with AI...
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Searching across Osceola, Orange, Polk, and Brevard counties
            </p>
          </div>
        )}

        {/* Pantry Cards */}
        {!isLoading && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence>
              {filteredPantries.map((pantry, index) => (
                <motion.div
                  key={pantry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Card Header */}
                  <div 
                    className="px-4 py-3 flex items-center justify-between"
                    style={{ backgroundColor: colors.forestGreen }}
                  >
                    <h3 className="text-white font-semibold truncate flex-1 mr-2">
                      {pantry.name}
                    </h3>
                    <span 
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        pantry.verified 
                          ? 'bg-green-400 text-green-900' 
                          : 'bg-gray-300 text-gray-700'
                      }`}
                    >
                      {pantry.verified ? 'Verified' : 'Unconfirmed'}
                    </span>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-gray-400 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-800">{pantry.address}</p>
                        <p className="text-sm text-gray-600">
                          {pantry.city}, FL {pantry.zip}
                        </p>
                        <span 
                          className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: `${colors.gold}30`, color: colors.forestGreen }}
                        >
                          {pantry.county} County
                        </span>
                      </div>
                    </div>

                    {pantry.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={16} className="text-gray-400" />
                        <a href={`tel:${pantry.phone}`} className="text-sm text-blue-600 hover:underline">
                          {pantry.phone}
                        </a>
                      </div>
                    )}

                    {pantry.hours && (
                      <div className="flex items-start gap-2">
                        <Clock size={16} className="text-gray-400 mt-0.5" />
                        <p className="text-sm text-gray-600">{pantry.hours}</p>
                      </div>
                    )}

                    {pantry.notes && (
                      <div className="flex items-start gap-2">
                        <FileText size={16} className="text-gray-400 mt-0.5" />
                        <p className="text-sm text-gray-500 italic">{pantry.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Card Actions */}
                  <div className="px-4 py-3 bg-gray-50 flex items-center justify-between border-t">
                    <button
                      onClick={() => handleToggleVerified(pantry)}
                      className={`flex items-center gap-1 text-xs font-medium ${
                        pantry.verified ? 'text-green-600' : 'text-gray-500'
                      }`}
                    >
                      {pantry.verified ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                      {pantry.verified ? 'Verified' : 'Mark Verified'}
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditPantry(pantry)}
                        className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeletePantry(pantry)}
                        className="p-2 rounded-lg hover:bg-red-100 text-red-500"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredPantries.length === 0 && (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700">No pantries found</h3>
            <p className="text-gray-500 mt-2">
              {searchQuery || selectedCounty !== 'All' 
                ? 'Try adjusting your filters or search terms'
                : 'Click "AI Refresh" to search for pantries'}
            </p>
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div 
                className="px-6 py-4 flex items-center justify-between"
                style={{ backgroundColor: colors.forestGreen }}
              >
                <h2 className="text-xl font-bold text-white">
                  {editingPantry ? 'Edit Pantry' : 'Add New Pantry'}
                </h2>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-white/80 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pantry Name *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Community Food Bank"
                    data-testid="modal-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <Input
                    value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main Street"
                    data-testid="modal-address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City *
                    </label>
                    <Input
                      value={formData.city}
                      onChange={e => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Orlando"
                      data-testid="modal-city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP Code
                    </label>
                    <Input
                      value={formData.zip}
                      onChange={e => setFormData({ ...formData, zip: e.target.value })}
                      placeholder="32801"
                      data-testid="modal-zip"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    County *
                  </label>
                  <select
                    value={formData.county}
                    onChange={e => setFormData({ ...formData, county: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    data-testid="modal-county"
                  >
                    {COUNTIES.filter(c => c !== 'All').map(county => (
                      <option key={county} value={county}>{county}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(407) 555-1234"
                    data-testid="modal-phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hours
                  </label>
                  <Input
                    value={formData.hours}
                    onChange={e => setFormData({ ...formData, hours: e.target.value })}
                    placeholder="Mon-Fri 9am-5pm"
                    data-testid="modal-hours"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="ID required, eligibility requirements, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 min-h-[80px]"
                    dir="ltr"
                    style={{ textAlign: 'left', direction: 'ltr' }}
                    data-testid="modal-notes"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="verified"
                    checked={formData.verified}
                    onChange={e => setFormData({ ...formData, verified: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="modal-verified"
                  />
                  <label htmlFor="verified" className="text-sm text-gray-700">
                    Mark as verified/active
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => setShowModal(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSavePantry}
                    className="flex-1 text-white"
                    style={{ backgroundColor: colors.forestGreen }}
                    data-testid="modal-save-btn"
                  >
                    <Save size={18} className="mr-2" />
                    Save Pantry
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
